import React, { useEffect, useRef, useState } from 'react';
import { Col, Container, Row, Form, Button, Image, InputGroup } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthenticationHeader from '../../components/shared/AuthenticationHeader';
import logo from "../../assets/logo.svg";
import leftimage from "../../assets/login.jpg";
import { GoogleAuthLogin, LoginForm } from 'services/authentication/authentication.service';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from 'utils/AuthContext';
import { TypeAnimation } from 'react-type-animation';
import { GoogleLogin } from '@react-oauth/google';
import Loader from 'components/layouts/Loader';
import { jwtDecode } from 'jwt-decode';
import { createSubmission } from 'services/Student/Submission.services';

const SafeLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { verifyAuth } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const assignmentId = params.get('assignmentId');
    const token = params.get('token');
    const hasRun = useRef(false);

    // 🔹 Auto-login if SEB token is present
    useEffect(() => {
        const autoLogin = async () => {
            if (token && !hasRun.current) {
                try {
                    hasRun.current = true;
                    const decoded = jwtDecode(token);

                    // Build user object with token included
                    const user = {
                        _id: decoded._id,
                        fullName: decoded.fullName,
                        email: decoded.email,
                        class: decoded.class,
                        role: decoded.role,
                        status: decoded.status,
                        imageUrl: decoded.imageUrl,
                        school: decoded.school,
                        phone: decoded.phone || "",
                        isVerified: decoded.isVerified ?? true,
                        credits: decoded.credits ?? 0,
                        token, // store actual JWT
                    };

                    // Save in localStorage
                    localStorage.setItem("user", JSON.stringify(user));

                    // Verify auth in context
                    await verifyAuth();

                    // Create submission & redirect
                    setLoading(true);
                    const response = await createSubmission({ assignmentId });
                    setLoading(false);
                    navigate(`/Safequiz/attempt/${response.data?.result?._id}`);
                } catch (err) {
                    console.error("Auto-login failed:", err);
                    setLoading(false);
                    navigate(`/not-exist`);
                }
            }
        };

        autoLogin();
    }, [token, assignmentId, navigate, verifyAuth]);

    // 🔹 Manual login
    const handleSubmit = async (event) => {
        event.preventDefault();
        const data = { email, password, assignmentId };
        const toastId = toast.loading('loading...');

        try {
            const response = await LoginForm(data);
            const user = response.data.result;

            toast.update(toastId, {
                render: 'User logged in successfully.',
                type: 'success',
                isLoading: false,
                autoClose: 3000,
            });

            localStorage.setItem('user', JSON.stringify(user));
            setLoading(true);
            await verifyAuth();

            const submission = await createSubmission({ assignmentId });
            setLoading(false);
            navigate(`/Safequiz/attempt/${submission.data?.result?._id}`);
        } catch (error) {
            toast.update(toastId, {
                render: `Error: ${error.response?.data?.error || "Login failed"}`,
                type: 'error',
                isLoading: false,
                autoClose: 5000,
            });
            setLoading(false);
            navigate(`/not-exist`);
        }
    };

    // 🔹 Google login
    const handleGoogleLogin = async (credentialResponse) => {
        try {
            const response = await GoogleAuthLogin({
                token: credentialResponse.credential,
                assignmentId,
            });
            const user = response.data.result;

            localStorage.setItem('user', JSON.stringify(user));
            toast.success('Logged in successfully');
            await verifyAuth();

            const submission = await createSubmission({ assignmentId });
            navigate(`/Safequiz/attempt/${submission.data?.result?._id}`);
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.error || 'Google SafeLogin failed');
            navigate(`/not-exist`);
        }
    };

    const handleTogglePassword = () => setShowPassword((prev) => !prev);

    if (loading) return <Loader />;

    return (
        <Container fluid style={{ minHeight: '100vh' }}>
            <Row>
                {/* Left Side Image */}
                <Col xs={12} md={6} className='p-0 d-none d-md-block position-relative'>
                    <Image
                        src={leftimage}
                        alt='Signup Illustration'
                        className='w-100'
                        style={{ height: '100vh', objectFit: 'cover' }}
                    />
                    <div
                        className='overlay position-absolute w-100 h-100'
                        style={{ top: 0, left: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
                    >
                        <div
                            className='text-overlay position-absolute d-flex flex-column justify-content-center align-items-start'
                            style={{ height: '100vh', left: '5%', top: 0 }}
                        >
                            <h1
                                className='mb-3'
                                style={{ fontSize: "50px", fontWeight: "800", color: "#ed4e4f" }}
                            >
                                Fun meets<br /> knowledge!
                            </h1>

                            <TypeAnimation
                                sequence={[
                                    'Learn While Playing Games!',
                                    1000,
                                    'Explore Animal Facts in Fun Quizzes',
                                    1000,
                                    'Test Your Knowledge with Challenges',
                                    1000,
                                    'Discover New Things with Every Level',
                                    1000,
                                ]}
                                wrapper="div"
                                speed={50}
                                style={{ fontSize: '32px', fontWeight: '600', color: 'white' }}
                                repeat={Infinity}
                            />
                        </div>
                    </div>
                </Col>

                {/* Right Side Login */}
                <Col xs={12} md={6} className='d-flex align-items-center justify-content-center p-4 authentication-form'>
                    <Col xs={12} lg={7}>
                        <AuthenticationHeader image={logo} text={'Log into your Account'} />
                        <Form onSubmit={handleSubmit} style={{ width: '100%' }}>
                            <Form.Group className='mb-3'>
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    type='email'
                                    placeholder='Enter email'
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className='mb-1 position-relative'>
                                <Form.Label>Password</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder='Enter password'
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                    <InputGroup.Text onClick={handleTogglePassword} style={{ cursor: 'pointer' }}>
                                        <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
                                    </InputGroup.Text>
                                </InputGroup>
                            </Form.Group>
                            <Button variant='primary' type='submit' className='mt-3 w-100'>
                                Sign In
                            </Button>
                            <hr />
                            <Row className="mt-3 google-btn justify-content-between align-items-center">
                                <Col xs={12} lg={6} className='p-0'>
                                    <GoogleLogin
                                        onSuccess={handleGoogleLogin}
                                        theme="filled_black"
                                        shape="pill"
                                        text="signin_with"
                                        width={90}
                                        useOneTap={true}
                                        onError={() => toast.error("Google Sign-In failed")}
                                    />
                                </Col>
                                <Col className='mt-3 mt-lg-0' xs={12} md={12} lg={6}>
                                    <Button
                                        style={{ fontSize: "15px", background: "#392C26" }}
                                        variant='primary'
                                        disabled={true}
                                        className='w-100 py-2 border-0'
                                    >
                                        Sign In with Microsoft
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Col>
                </Col>
            </Row>
        </Container>
    );
};

export default SafeLogin;
